import { useState, useRef, useEffect } from 'react';
import { supabase } from '../../../../lib/supabase';
import { useAuth } from '../../../../hooks/useAuth';

interface ProfileHeaderProps {
  displayName: string;
  username: string;
  bio: string;
  joinDate: string;
  avatarUrl: string;
  onAvatarUpdate?: (url: string) => void;
  onUsernameUpdate?: (username: string) => void;
  onBioUpdate?: (bio: string) => Promise<void>;
  onEditClick?: () => void;
}

export default function ProfileHeader({
  displayName,
  username,
  bio,
  joinDate,
  avatarUrl,
  onAvatarUpdate,
  onUsernameUpdate,
  onBioUpdate,
}: ProfileHeaderProps) {
  const { user } = useAuth();

  // Avatar upload
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [cropPosition, setCropPosition] = useState({ x: 0, y: 0 });
  const [cropScale, setCropScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // Username edit
  const [editingUsername, setEditingUsername] = useState(false);
  const [usernameValue, setUsernameValue] = useState(username || '');
  const [usernameError, setUsernameError] = useState('');
  const [savingUsername, setSavingUsername] = useState(false);

  // Bio edit
  const [editingBio, setEditingBio] = useState(false);
  const [bioValue, setBioValue] = useState(bio || '');
  const [savingBio, setSavingBio] = useState(false);

  useEffect(() => {
    setUsernameValue(username || '');
  }, [username]);

  useEffect(() => {
    setBioValue(bio || '');
  }, [bio]);

  // --- Avatar ---
  const handleAvatarClick = () => fileInputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setUploadError('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { setUploadError('Image must be less than 5MB'); return; }
    setUploadError('');
    const reader = new FileReader();
    reader.onload = (ev) => {
      setSelectedImage(ev.target?.result as string);
      setShowCropper(true);
      setCropPosition({ x: 0, y: 0 });
      setCropScale(1);
    };
    reader.readAsDataURL(file);
  };

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image || !selectedImage) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const size = 300;
    canvas.width = size;
    canvas.height = size;
    ctx.fillStyle = '#18181b';
    ctx.fillRect(0, 0, size, size);
    ctx.drawImage(image, cropPosition.x, cropPosition.y, image.naturalWidth * cropScale, image.naturalHeight * cropScale);
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 2;
    ctx.strokeRect(0, 0, size, size);
  };

  useEffect(() => {
    if (showCropper && selectedImage) drawCanvas();
  }, [showCropper, selectedImage, cropPosition, cropScale]);

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - cropPosition.x, y: e.clientY - cropPosition.y });
  };
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging) return;
    setCropPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const handleMouseUp = () => setIsDragging(false);

  const handleCropConfirm = async () => {
    if (!canvasRef.current || !user) return;
    setIsUploading(true);
    try {
      const blob = await new Promise<Blob>((resolve) =>
        canvasRef.current!.toBlob((b) => resolve(b!), 'image/jpeg', 0.9)
      );
      const fileName = `${user.id}/avatar.jpg`;
      const { error: uploadErr } = await supabase.storage
        .from('avatars')
        .upload(fileName, blob, { upsert: true, contentType: 'image/jpeg' });
      if (uploadErr) throw uploadErr;
      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(fileName);
      onAvatarUpdate?.(publicUrl);
      setShowCropper(false);
      setSelectedImage(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch {
      setUploadError('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setSelectedImage(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // --- Username ---
  const validateUsername = (val: string) => {
    if (!val.trim()) return 'Username is required';
    if (val.length < 3) return 'At least 3 characters';
    if (val.length > 30) return 'Max 30 characters';
    if (!/^[a-zA-Z0-9_]+$/.test(val)) return 'Letters, numbers, underscores only';
    return '';
  };

  const handleUsernameSave = async () => {
    const err = validateUsername(usernameValue);
    if (err) { setUsernameError(err); return; }
    setSavingUsername(true);
    try {
      await onUsernameUpdate?.(usernameValue);
      setEditingUsername(false);
      setUsernameError('');
    } catch {
      setUsernameError('Failed to save username');
    } finally {
      setSavingUsername(false);
    }
  };

  // --- Bio ---
  const handleBioSave = async () => {
    setSavingBio(true);
    try {
      await onBioUpdate?.(bioValue);
      setEditingBio(false);
    } catch {
      // silently fail
    } finally {
      setSavingBio(false);
    }
  };

  const resolvedAvatar = avatarUrl ||
    `https://public.readdy.ai/ai/img_res/7a2d963ceddafce496718d80efd61b1e.jpg`;

  return (
    <>
      {/* Cropper Modal */}
      {showCropper && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-red-900/30 rounded-lg p-6 w-full max-w-md space-y-4">
            <h3 className="text-lg font-semibold text-zinc-100 font-serif">Crop Profile Picture</h3>
            <div className="flex flex-col items-center space-y-4">
              <canvas
                ref={canvasRef}
                className="border-2 border-red-900/50 rounded-lg cursor-move"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              />
              <div className="w-full max-w-xs">
                <label className="block text-sm text-zinc-400 mb-1">Zoom</label>
                <input
                  type="range" min="0.5" max="3" step="0.1" value={cropScale}
                  onChange={(e) => setCropScale(parseFloat(e.target.value))}
                  className="w-full accent-red-500"
                />
              </div>
              {uploadError && <p className="text-sm text-red-400">{uploadError}</p>}
              <div className="flex gap-3">
                <button
                  type="button" onClick={handleCropCancel} disabled={isUploading}
                  className="px-5 py-2 text-sm text-zinc-300 hover:bg-zinc-800 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button" onClick={handleCropConfirm} disabled={isUploading}
                  className="px-5 py-2 text-sm text-white bg-red-700 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
                >
                  {isUploading ? 'Uploading...' : 'Confirm'}
                </button>
              </div>
            </div>
            {selectedImage && (
              <img ref={imageRef} src={selectedImage} alt="Selected" className="hidden" onLoad={drawCanvas} />
            )}
          </div>
        </div>
      )}

      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />

      <div className="bg-zinc-950 border border-red-900/30 rounded-lg p-8 mb-8">
        <div className="flex flex-col md:flex-row gap-8 items-start">
          {/* Clickable Avatar */}
          <div
            className="relative w-32 h-32 flex-shrink-0 group cursor-pointer"
            onClick={handleAvatarClick}
            title="Click to change profile picture"
          >
            <img
              src={resolvedAvatar}
              alt={displayName}
              className="w-full h-full rounded-full object-cover border-2 border-red-900/50 transition-opacity group-hover:opacity-70"
            />
            <div className="absolute inset-0 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="bg-black/60 rounded-full p-2">
                <i className="ri-camera-line text-white text-xl"></i>
              </div>
            </div>
          </div>

          <div className="flex-1">
            {/* Display Name (editable) */}
            <div className="flex items-center gap-2 mb-2">
              {editingUsername ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={usernameValue}
                    onChange={(e) => { setUsernameValue(e.target.value); setUsernameError(''); }}
                    className="bg-zinc-800 border border-red-900/50 text-zinc-100 text-2xl font-bold rounded px-2 py-1 focus:outline-none focus:border-red-500 w-56 font-serif"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleUsernameSave();
                      if (e.key === 'Escape') { setEditingUsername(false); setUsernameValue(username || ''); }
                    }}
                  />
                  <button
                    onClick={handleUsernameSave} disabled={savingUsername}
                    className="w-7 h-7 flex items-center justify-center text-green-400 hover:text-green-300 cursor-pointer disabled:opacity-50"
                  >
                    <i className="ri-check-line text-lg"></i>
                  </button>
                  <button
                    onClick={() => { setEditingUsername(false); setUsernameValue(username || ''); setUsernameError(''); }}
                    className="w-7 h-7 flex items-center justify-center text-zinc-400 hover:text-zinc-200 cursor-pointer"
                  >
                    <i className="ri-close-line text-lg"></i>
                  </button>
                </div>
              ) : (
                <>
                  <h1 className="text-3xl font-bold text-red-50 font-serif">{displayName}</h1>
                  <button
                    onClick={() => { setEditingUsername(true); setUsernameValue(displayName || ''); }}
                    className="w-7 h-7 flex items-center justify-center text-zinc-500 hover:text-red-400 transition-colors cursor-pointer"
                    title="Edit name"
                  >
                    <i className="ri-pencil-line text-sm"></i>
                  </button>
                </>
              )}
            </div>
            {usernameError && <p className="text-xs text-red-400 mb-1">{usernameError}</p>}

            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-red-900/20 border border-red-900/50 rounded-full text-red-400 text-sm font-medium">
                Reader
              </span>
              <span className="text-zinc-500 text-sm">Joined {joinDate}</span>
            </div>

            {/* Bio */}
            {editingBio ? (
              <div className="space-y-2">
                <textarea
                  value={bioValue}
                  onChange={(e) => setBioValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape') { setEditingBio(false); setBioValue(bio || ''); } }}
                  className="w-full bg-zinc-800 border border-red-900/50 text-zinc-300 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-red-500 resize-none"
                  rows={3}
                  placeholder="Tell us about yourself..."
                  autoFocus
                  maxLength={500}
                />
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleBioSave}
                    disabled={savingBio}
                    className="px-4 py-1.5 bg-red-700 text-white text-sm rounded-lg hover:bg-red-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    {savingBio ? 'Saving...' : 'Save Bio'}
                  </button>
                  <button
                    onClick={() => { setEditingBio(false); setBioValue(bio || ''); }}
                    className="px-4 py-1.5 bg-zinc-800 text-zinc-300 text-sm rounded-lg hover:bg-zinc-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2 group">
                <p className="text-zinc-400 leading-relaxed flex-1">
                  {bio || 'No bio yet.'}
                </p>
                <button
                  onClick={() => setEditingBio(true)}
                  className="w-7 h-7 flex items-center justify-center text-zinc-600 hover:text-red-400 transition-colors cursor-pointer opacity-0 group-hover:opacity-100 flex-shrink-0"
                  title="Edit bio"
                >
                  <i className="ri-pencil-line text-sm"></i>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
